import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { X, Save, Loader2, Play, Rocket, CheckCircle2, XCircle, ChevronRight } from "lucide-react";
import { api } from "@/lib/api";
import { useAccounts } from "@/context/AccountContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";

const TFS = ["M15", "M30", "H1", "H4", "D1"];
const SYMBOLS = ["EURUSD", "GBPUSD", "USDJPY", "XAUUSD", "AUDUSD", "USDCHF", "USDCAD"];

const dayISO = (offset) => new Date(Date.now() - offset * 24 * 3600 * 1000).toISOString();

export default function BacktestWizard({ open, onClose }) {
  const { activeAccountId } = useAccounts();
  const [form, setForm] = useState({
    symbol: "EURUSD", timeframe: "H1", starting_balance: 10000,
    use_learned_weights: false,
    start_date: dayISO(60), end_date: dayISO(0),
  });
  const [running, setRunning] = useState(false);
  const [pollingId, setPollingId] = useState(null);
  const [status, setStatus] = useState(null);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);
  const navigate = useNavigate();

  const loadHistory = async () => {
    try {
      const r = await api.get("/backtest");
      setHistory(r.data?.backtests || []);
    } catch { /* silent */ }
  };
  useEffect(() => { if (open) loadHistory(); }, [open]);

  const submit = async () => {
    setRunning(true); setStatus(null); setResult(null);
    try {
      const r = await api.post("/backtest", {
        account_id: activeAccountId,
        symbol: form.symbol.toUpperCase(),
        timeframe: form.timeframe,
        start_date: form.start_date,
        end_date: form.end_date,
        starting_balance: Number(form.starting_balance) || 10000,
        use_learned_weights: !!form.use_learned_weights,
      });
      const id = r.data?.backtest_id;
      setPollingId(id);
      setStatus({ status: "running", progress_pct: 0 });
      toast.success("Backtest started");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to start backtest");
      setRunning(false);
    }
  };

  useEffect(() => {
    if (!pollingId) return;
    let alive = true;
    const iv = setInterval(async () => {
      try {
        const r = await api.get(`/backtest/${pollingId}`);
        if (!alive) return;
        setStatus({ status: r.data?.status, progress_pct: r.data?.progress_pct });
        if (r.data?.status === "completed") {
          setResult(r.data);
          setRunning(false);
          clearInterval(iv);
          setPollingId(null);
          toast.success("Backtest completed");
          loadHistory();
        } else if (r.data?.status === "failed") {
          setRunning(false);
          setResult(r.data);
          clearInterval(iv);
          setPollingId(null);
          toast.error(r.data?.error || "Backtest failed");
        }
      } catch { /* silent */ }
    }, 2000);
    return () => { alive = false; clearInterval(iv); };
  }, [pollingId]);

  const results = result?.results || {};

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl bg-[#0a0e1a] border-[#1f2937] text-[#e5e7eb]" data-testid="backtest-wizard">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl tracking-tight flex items-center gap-2">
            <Rocket className="w-5 h-5 text-[#3b82f6]" /> Backtest Engine
          </DialogTitle>
          <DialogDescription className="text-xs text-[#9ca3af]">
            Replay the detection + risk pipeline over any historical window.
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <label className="vx-label mb-1 block">Symbol</label>
            <Select value={form.symbol} onValueChange={(v) => setForm({ ...form, symbol: v })}>
              <SelectTrigger className="bg-[#111827] border-[#1f2937] font-mono text-xs" data-testid="bt-symbol">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#0a0e1a] border-[#1f2937] text-[#e5e7eb]">
                {SYMBOLS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="vx-label mb-1 block">Timeframe</label>
            <Select value={form.timeframe} onValueChange={(v) => setForm({ ...form, timeframe: v })}>
              <SelectTrigger className="bg-[#111827] border-[#1f2937] font-mono text-xs" data-testid="bt-timeframe">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#0a0e1a] border-[#1f2937] text-[#e5e7eb]">
                {TFS.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="vx-label mb-1 block">Starting balance ($)</label>
            <Input
              type="number" value={form.starting_balance}
              onChange={(e) => setForm({ ...form, starting_balance: e.target.value })}
              className="bg-[#111827] border-[#1f2937] font-mono text-xs"
              data-testid="bt-starting-balance"
            />
          </div>
          <div>
            <label className="vx-label mb-1 block">Start date</label>
            <Input
              type="date"
              value={form.start_date.slice(0, 10)}
              onChange={(e) => setForm({ ...form, start_date: new Date(e.target.value).toISOString() })}
              className="bg-[#111827] border-[#1f2937] font-mono text-xs"
              data-testid="bt-start-date"
            />
          </div>
          <div>
            <label className="vx-label mb-1 block">End date</label>
            <Input
              type="date"
              value={form.end_date.slice(0, 10)}
              onChange={(e) => setForm({ ...form, end_date: new Date(e.target.value).toISOString() })}
              className="bg-[#111827] border-[#1f2937] font-mono text-xs"
              data-testid="bt-end-date"
            />
          </div>
          <div className="flex items-end">
            <div className="flex items-center gap-2 h-9">
              <Switch
                checked={form.use_learned_weights}
                onCheckedChange={(v) => setForm({ ...form, use_learned_weights: v })}
                data-testid="bt-use-learned"
              />
              <span className="text-xs text-[#9ca3af]">Use learned weights</span>
            </div>
          </div>
        </div>

        <div className="pt-2 flex items-center gap-2">
          <button
            onClick={submit} disabled={running || !activeAccountId}
            data-testid="bt-run-btn"
            className="h-9 px-4 rounded bg-[#3b82f6] hover:bg-[#2563eb] text-white text-xs font-mono flex items-center gap-1 disabled:opacity-60"
          >
            {running ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Play className="w-3.5 h-3.5" />}
            {running ? `Running… ${status?.progress_pct || 0}%` : "Run Backtest"}
          </button>
        </div>

        {status?.status === "running" && (
          <div className="mt-2 h-1.5 rounded-full bg-[#1f2937] overflow-hidden" data-testid="bt-progress-bar">
            <div className="h-full bg-[#3b82f6] transition-all"
                 style={{ width: `${status.progress_pct || 0}%` }} />
          </div>
        )}

        {result && result.status === "completed" && (
          <div className="mt-3 vx-card p-4 space-y-3" data-testid="bt-result-summary">
            <div className="vx-label">Result</div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs font-mono">
              <div><div className="text-[#6b7280]">Signals detected</div><div className="text-[#e5e7eb] font-bold text-sm">{results.total_signals_detected}</div></div>
              <div><div className="text-[#6b7280]">Trades</div><div className="text-[#e5e7eb] font-bold text-sm">{results.total_trades} <span className="text-[#6b7280]">({results.wins}W/{results.losses}L)</span></div></div>
              <div><div className="text-[#6b7280]">Win rate</div><div className="text-[#e5e7eb] font-bold text-sm">{results.win_rate_pct}%</div></div>
              <div><div className="text-[#6b7280]">P&amp;L</div><div className="font-bold text-sm" style={{ color: results.total_pnl >= 0 ? "#10b981" : "#ef4444" }}>{results.total_pnl >= 0 ? "+" : ""}{results.total_pnl_pct}%</div></div>
              <div><div className="text-[#6b7280]">Max DD</div><div className="text-[#ef4444] font-bold text-sm">{results.max_drawdown_pct}%</div></div>
              <div><div className="text-[#6b7280]">Profit factor</div><div className="text-[#e5e7eb] font-bold text-sm">{results.profit_factor}</div></div>
              <div><div className="text-[#6b7280]">Avg R</div><div className="text-[#e5e7eb] font-bold text-sm">{results.avg_rr_realized}</div></div>
              <div><div className="text-[#6b7280]">Ending balance</div><div className="text-[#e5e7eb] font-bold text-sm">${(results.ending_balance || 0).toLocaleString()}</div></div>
            </div>
            {(results.per_pattern_breakdown || []).length > 0 && (
              <div className="mt-2 text-xs">
                <div className="vx-label mb-1">Per-pattern edge</div>
                <table className="w-full font-mono text-[11px]" data-testid="bt-per-pattern">
                  <thead className="text-left text-[#6b7280]">
                    <tr><th className="py-1">Pattern</th><th className="py-1 text-right">Trades</th><th className="py-1 text-right">Win%</th><th className="py-1 text-right">P&amp;L</th></tr>
                  </thead>
                  <tbody>
                    {results.per_pattern_breakdown.map((r) => (
                      <tr key={r.pattern_type} className="border-t border-[#1f2937]">
                        <td className="py-1 text-[#e5e7eb] font-bold">{r.pattern_type}</td>
                        <td className="py-1 text-right">{r.trades}</td>
                        <td className="py-1 text-right" style={{ color: r.win_rate >= 50 ? "#10b981" : "#f59e0b" }}>{r.win_rate}%</td>
                        <td className="py-1 text-right font-bold" style={{ color: r.pnl >= 0 ? "#10b981" : "#ef4444" }}>{r.pnl >= 0 ? "+" : ""}${Math.abs(r.pnl).toFixed(0)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
        {result && result.status === "failed" && (
          <div className="mt-3 vx-card p-4 border-[#ef4444]/40 bg-[#ef4444]/10" data-testid="bt-failed">
            <div className="flex items-center gap-2 text-[#ef4444] text-xs font-mono">
              <XCircle className="w-3.5 h-3.5" /> Backtest failed: {result.error}
            </div>
          </div>
        )}

        <div className="pt-2 border-t border-[#1f2937]">
          <div className="vx-label mb-2">Recent backtests</div>
          {history.length === 0 ? (
            <div className="text-xs text-[#6b7280] font-mono py-3">No backtests yet.</div>
          ) : (
            <div className="max-h-[180px] overflow-y-auto space-y-1">
              {history.map((h) => (
                <div key={h.id} className="flex items-center justify-between p-2 rounded border border-[#1f2937] hover:bg-[#1f2937]/50 text-xs font-mono">
                  <div className="flex-1 min-w-0">
                    <div className="text-[#e5e7eb] font-bold">{h.symbol} · {h.timeframe}</div>
                    <div className="text-[#6b7280] text-[10px]">{h.started_at?.slice(0,16)} · {h.status}</div>
                  </div>
                  {h.results && (
                    <div className="text-right" style={{ color: (h.results.total_pnl_pct || 0) >= 0 ? "#10b981" : "#ef4444" }}>
                      {(h.results.total_pnl_pct >= 0 ? "+" : "") + (h.results.total_pnl_pct ?? 0)}%
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
